import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useConfigStore } from '@/stores/config'
import { useRequestsStore } from '@/stores/requests'
import { useTokenStore } from '@/stores/token'
import getRequestParams from '@/helpers/getRequestParams'

export const useUsersStore = defineStore('users', () => {
    const configStore = useConfigStore()
    const requestsStore = useRequestsStore()
    const tokenStore = useTokenStore()

    const users = ref({ array: [] })
    const usersProcess = ref(false)
    const usersMsg = ref({})

    const user = ref({})
    const userProcess = ref(false)
    const userMsg = ref({})

    const userTypes = ref(['USER_TYPE_USER', 'USER_TYPE_EXPERT', 'USER_TYPE_SUPER'])

    const userAttributes = ref([
        'LOGS_CAR',
        'EDIT_PRIVATE_CAR',
        'GEODATA',
        'REMOTE_CONTROL',
        'DIAGNOSTIC',
        'PARAM',
        'FIRMWARE',
        'COMMAND',
        'GROUP_ADMIN',
        'SUPER_ADMIN'
    ])

    const usersGroups = ref({ array: [] })
    const usersGroupsProcess = ref(false)
    const usersGroupsMsg = ref({})

    /**
     * Создаёт нового пользователя
     *
     * @param {Object} payload - объект с полями формы создания нового пользователя
     */
    async function createUser(payload) {
        const token = tokenStore.access

        if (token) {
            userProcess.value = true

            if (payload.newGroup) {
                const request = await requestsStore.fetchRequest({
                    address: `${configStore.httpServer}/ugroups`,
                    method: 'POST',
                    token,
                    data: payload.newGroup
                })

                if (request.uuid) {
                    payload.ugroup = request.uuid
                    delete payload.newGroup
                } else {
                    userMsg.value = { success: false, text: request.error }
                    userProcess.value = false
                }
            }

            const request = await requestsStore.fetchRequest({
                address: `${configStore.httpServer}/users`,
                method: 'POST',
                token,
                data: payload
            })

            if (!request.error) {
                userMsg.value = { success: true, text: 'USER_CREATE_SUCCESS' }
            } else {
                userMsg.value = { success: false, text: request.error }
            }

            userProcess.value = false
        } else {
            userMsg.value = { success: false, text: getters.getTranslate.EUnauthRequest }
        }
    }

    /**
     * Получает пользователей
     *
     * @param {String} payload - параметры запроса [см.swagger]
     */
    async function readUsers(payload) {
        const token = tokenStore.access

        if (token) {
            usersProcess.value = true

            let params = getRequestParams(payload)

            const request = await requestsStore.fetchRequest({
                address: `${configStore.httpServer}/users${params}`,
                method: 'GET',
                token
            })

            if (!request.error) {
                users.value = request

                // Получаем группу пользователей по UUID групп
                if (users.value.array.length) {
                    let usersUuids = []
                    users.value.array.map(user => (user.ugroup ? usersUuids.push(user.ugroup) : false))

                    if (usersUuids.length) {
                        let params = ''
                        usersUuids.map(uuid =>
                            params.length ? (params += '&uuid_array=' + uuid) : (params = '?uuid_array=' + uuid)
                        )

                        const request = await requestsStore.fetchRequest({
                            address: `${configStore.httpServer}/ugroups${params}`,
                            method: 'GET',
                            token
                        })

                        if (!request.error && request.array.length) {
                            request.array.map(group => {
                                users.value.array.map(user => {
                                    if (user.ugroup == group.uuid) {
                                        user.group = group
                                    }
                                })
                            })
                        }
                    }
                }

                usersMsg.value = { success: true }
            } else {
                users.value = { array: [] }
                usersMsg.value = { success: false, text: request.error }
            }

            usersProcess.value = false
        } else {
            usersMsg.value = { success: false, text: 'EUnauthRequest' }
        }

        return users.value
    }

    /**
     * Получает пользователя по UUID
     *
     * @param {String} payload - UUID пользователя в системе
     */
    async function readUser(payload) {
        const token = tokenStore.access

        if (token) {
            userProcess.value = true

            const request = await requestsStore.fetchRequest({
                address: `${configStore.httpServer}/users/${payload}`,
                method: 'GET',
                token
            })

            if (!request.error) {
                user.value = request
                userMsg.value = { success: true }
            } else {
                user.value = {}
                userMsg.value = { success: false, text: request.error }
            }

            userProcess.value = false
        } else {
            userMsg.value = { success: false, text: 'EUnauthRequest' }
        }

        return user.value
    }

    /**
     * Обновляет профиль пользователя
     *
     * @param {Object} payload - объект с полями формы профиля пользователя
     */
    async function updateUser(payload) {
        const token = tokenStore.access

        if (token) {
            userProcess.value = true

            const request = await requestsStore.fetchRequest({
                address: `${configStore.httpServer}/users`,
                method: 'PUT',
                token,
                data: payload
            })

            if (!request.error) {
                user.value = request
                userMsg.value = {
                    success: true,
                    text: 'USER_UPDATE_SUCCESS',
                    logout: request.sessionIsDeleted
                }
            } else {
                userMsg.value = { success: false, text: request.error }
            }

            userProcess.value = false
        } else {
            userMsg.value = { success: false, text: 'EUnauthRequest' }
        }
    }

    /**
     * Удаление пользователя
     *
     * @param {Object} payload - ID пользователя
     */
    async function deleteUser(payload) {
        const token = tokenStore.access

        if (token) {
            userProcess.value = true

            const request = await requestsStore.fetchRequest({
                address: `${configStore.httpServer}/users/${payload}`,
                method: 'DELETE',
                token
            })

            if (!request.error) {
                userMsg.value = { success: true, text: 'USER_DELETE_SUCCESS' }
                readUsers()
            } else {
                userMsg.value = { success: false, text: request.error }
            }

            userProcess.value = false
        } else {
            userMsg.value = { success: false, text: 'EUnauthRequest' }
        }
    }

    return {
        users,
        usersProcess,
        usersMsg,
        user,
        userProcess,
        userMsg,
        userTypes,
        userAttributes,
        usersGroups,
        usersGroupsProcess,
        usersGroupsMsg,
        createUser,
        readUsers,
        readUser,
        updateUser,
        deleteUser
    }
})
